<template>
  <div class="fab" ref="fab">
    <template v-if="isMobile">
      <button class="fab__button" aria-label="Hilfedialog anzeigen" @click="toggleInfo">
        <IconHelp aria-hidden="true" />
      </button>
      <el-dialog v-model="showInfo" :show-close="false" :lock-scroll="isMobile ? true : false">
        <ContactFABContent @closeInfo="closeInfo" />
      </el-dialog>
    </template>
    <template v-else>
      <el-popover
        @after-enter="activate()"
        @before-leave="deactivate()"
        trigger="click"
        placement="top-end"
        :teleported="false"
        width="320px"
      >
        <template #reference>
          <button class="fab__button" aria-label="Hilfedialog anzeigen">
            <IconHelp aria-hidden="true" />
          </button>
        </template>
        <ContactFABContent ref="dialogRef" />
      </el-popover>
    </template>
  </div>
</template>

<script setup>
import ContactFABContent from '@/components/Booking/ContactFABContent.vue'
import IconHelp from '@/components/icons/IconHelp.vue'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

// State
const showInfo = ref(false)
const dialogRef = ref(null)
const fab = ref(null)
const { activate, deactivate } = useFocusTrap(dialogRef, { allowOutsideClick: true })

// Store
const store = useStore()
const isMobile = computed(() => store.getters.isMobile)

// Methods
const toggleInfo = () => {
  showInfo.value = !showInfo.value
}

const closeInfo = () => {
  showInfo.value = false
}

// Handle clicks outside dialog
const handleClickOutside = (event) => {
  if (showInfo.value && fab.value && !fab.value.contains(event.target)) {
    closeInfo()
  }
}

// Handle escape key
const handleEscapeKey = (event) => {
  if (showInfo.value && event.key === 'Escape') {
    closeInfo()
  }
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscapeKey)
})

onBeforeUnmount(() => {
  deactivate()
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscapeKey)
})
</script>

<style lang="scss">
.fab {
  position: fixed;
  bottom: 32px;
  right: 16px;
  z-index: $zindex-page-header;

  @media (min-width: $breakpoint-mobile) {
    bottom: 32px;
    right: 64px;
  }

  .el-dialog__body {
    padding: 0 !important;
  }

  &__button {
    background-color: $color-yellow-primary;
    border-radius: 50%;
    box-shadow: 0 3px 5px -1px rgba(0, 0, 0, 0.2), 0 1px 18px 0 rgba(0, 0, 0, 0.12),
      0 6px 10px 0 rgba(0, 0, 0, 0.14);
    cursor: pointer;
    height: 56px;
    width: 56px;
    padding: 12px;

    &:hover {
      background-color: $color-yellow-dark;
    }
  }

  &__popup {
    &__header {
      @include flex-center-align;
      justify-content: space-between;
      color: $color-text-secondary;
      padding: 16px;

      svg {
        cursor: pointer;
      }
    }

    &__body {
      font-size: 16px;
      padding: 16px 16px 0;
    }

    &__text {
      color: $color-text-secondary;
      margin-bottom: 8px;
      text-align: left;
      word-break: break-word;
    }

    &__section {
      @include flex-center-align;
      gap: 12px;
      padding: 16px 0;

      p {
        font-weight: $font-weight-medium;
      }

      &__right {
        margin-left: auto;
      }

      &__separator {
        position: absolute;
        right: 0;
        width: calc(100% - 53px);
      }

      &.unclickable {
        cursor: default;
      }
    }
  }
}
</style>
